import {
  computed,
  type ComputedRef,
  type MaybeRefOrGetter,
  type Ref,
  type ShallowRef,
  watch,
} from "vue";

import {
  useAssetByUrl,
  useContentItemByReferer,
  useSingleContentByUrn,
} from "@/composition/api/content.ts";
import { useAssetData } from "@/composition/archive/useAssetData.ts";

type UseImageAssetOptions = Partial<{
  immediate: boolean;
  refetch: boolean;
  onlyThumbnail: boolean;
}>;

interface UseImageAssetReturn {
  execute: (throwOnFailed?: boolean) => Promise<any>;
  data: ShallowRef<string | null>;
  error?: ShallowRef<any>;
  statusCode?: ShallowRef<number | null>;
  metadata?: ComputedRef<Record<string, unknown>>;
  isFinished?: ShallowRef<boolean>;
  locked?: ComputedRef<boolean>;
}
export function useImageAsset(
  urn: Ref<string>,
  parentUrn: MaybeRefOrGetter<string | null>,
  options: UseImageAssetOptions = {},
): UseImageAssetReturn {
  const { immediate = false, refetch = false, onlyThumbnail = false } = options;
  if (onlyThumbnail && parentUrn) {
    const {
      data: rawData,
      execute,
      isFinished,
      error,
      statusCode,
    } = useContentItemByReferer(parentUrn, urn, {
      refetch,
      immediate,
    });

    const { thumbnailRendition } = useAssetData(rawData);
    const data = computed(() => thumbnailRendition.value?.presignedUrl ?? null);

    const locked = computed(() => {
      return rawData.value?.referrerInfo?.accessLevel === "RESTRICTED";
    });

    return {
      execute,
      data,
      locked,
      isFinished,
      error,
      statusCode,
    };
  }

  const {
    execute,
    data: contentItem,
    error: metadataError,
  } = useSingleContentByUrn(urn, {
    immediate,
    refetch,
  });

  const { masonryRendition, metadata } = useAssetData(contentItem);

  const imageUrl = computed(() => masonryRendition.value?.url || "");
  const {
    execute: getAsset,
    data,
    error: renditionError,
    isFinished,
  } = useAssetByUrl(imageUrl);

  const locked = computed(() => {
    return contentItem.value?.referrerInfo?.accessLevel === "RESTRICTED";
  });

  const error = computed(() => {
    // If the error from useSingleContentByUrn or useAssetByUrl is a 404, return a specific error
    if (
      metadataError.value?.response?.status === 404 ||
      renditionError.value?.response?.status === 404
    ) {
      return new Error("Image not found (404)");
    }
    return metadataError.value ?? renditionError.value;
  });

  watch(
    () => masonryRendition.value?.url,
    async (newUrl) => {
      if (newUrl) {
        await getAsset();
      }
    },
  );

  return {
    execute,
    data,
    error,
    metadata,
    isFinished,
    locked,
  };
}
